import { ROUTE_PERMISSIONS } from "../config/routePermissions";
import type { RoutePermission } from "../types/auth.types";
import { userHasRouteAccess } from "./roleUtils";

/**
 * Converts a route pattern like "/:vertical/inquiries" into a RegExp.
 */
function patternToRegExp(pattern: string): RegExp {
  const regexSource = pattern
    .split("/")
    .map((segment) => {
      if (segment.startsWith(":")) {
        return "[^/]+";
      }
      return segment.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    })
    .join("/");

  return new RegExp(`^${regexSource}/?$`);
}

/**
 * Finds the most specific route permission for a pathname.
 * Supports exact/pattern matches and prefix inheritance for nested paths.
 */
export function findRoutePermission(pathname: string): RoutePermission | null {
  const normalizedPath = pathname.replace(/\/+$/, "") || "/";

  let bestMatch: RoutePermission | null = null;
  let bestScore = -1;

  for (const permission of ROUTE_PERMISSIONS) {
    const regex = patternToRegExp(permission.path);
    const isPatternMatch = regex.test(normalizedPath);
    const isPrefixMatch =
      normalizedPath === permission.path ||
      normalizedPath.startsWith(`${permission.path}/`);

    if (!isPatternMatch && !isPrefixMatch) {
      continue;
    }

    const score = permission.path.split("/").filter(Boolean).length;
    if (score > bestScore) {
      bestScore = score;
      bestMatch = permission;
    }
  }

  return bestMatch;
}

/**
 * Determines whether a user may access a given pathname.
 * Routes without an explicit permission entry require authentication only.
 */
export function canAccessPath(pathname: string, userRole: string | null): boolean {
  const permission = findRoutePermission(pathname);

  if (!permission) {
    return true;
  }

  return userHasRouteAccess(userRole, permission.allowedRoles);
}
